import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Map as LeafletMap } from 'leaflet'
import { apiGet } from '../../utils/api'
import { VisitorMap } from './VisitorMap'
import { VisitorDetailCard, VisitorAvatar } from './VisitorDetailCard'
import { VisitorDetailModal } from './VisitorDetailModal'
import { useLiveVisitors } from './useLiveVisitors'
import { LoadingSpinner } from '../shared/LoadingSpinner'
import {
  type MapVisitor,
  type VisitorSearchResponse,
  isPlottable,
  isVisitorOnline,
  sortRoster,
  visitorLocationLabel,
  visitorDisplayName,
} from './visitorMap.helpers'
import { formatRelativeTime } from '../../utils/format'

const SEARCH_DEBOUNCE_MS = 300
const FLY_ZOOM = 6

/** One row in the left-hand visitor list. */
const VisitorRow: React.FC<{
  visitor: MapVisitor
  active: boolean
  onClick: () => void
}> = ({ visitor, active, onClick }) => {
  const online = isVisitorOnline(visitor)
  // Last KNOWN location shown for both online and offline rows.
  const location = visitorLocationLabel(visitor)
  const secondary = [location, visitor.company_name]
    .filter((p): p is string => Boolean(p && p.trim()))
    .join(' · ')
  // For offline rows, a muted "last seen <relative>" hint.
  const lastSeen = visitor.last_seen_at || visitor.disconnected_at
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
        active
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
          : 'border-transparent hover:bg-surface-hover'
      }`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="relative shrink-0">
          <VisitorAvatar visitor={visitor} size={32} />
          <span
            className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${
              online ? 'bg-success animate-pulse-soft' : 'bg-txt-tertiary'
            }`}
            title={online ? 'Online' : 'Offline'}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-txt-primary truncate">
            {visitorDisplayName(visitor)}
          </p>
          <p className="text-[11px] text-txt-tertiary truncate">
            {secondary || 'Unknown location'}
          </p>
          {!online && lastSeen && (
            <p className="text-[10px] text-txt-tertiary truncate">
              last seen {formatRelativeTime(lastSeen)}
            </p>
          )}
        </div>
        {!isPlottable(visitor) && (
          <span
            className="text-[10px] text-txt-tertiary shrink-0"
            title="No location available — cannot be plotted on the map"
          >
            no geo
          </span>
        )}
      </div>
    </button>
  )
}

const VisitorMapViewInner: React.FC = () => {
  const { visitors, onlineCount, loading, error } = useLiveVisitors()

  const mapRef = useRef<LeafletMap | null>(null)
  const [selected, setSelected] = useState<MapVisitor | null>(null)
  // The full "Visitor details" drawer (total time, chats, companies).
  const [detailVisitor, setDetailVisitor] = useState<MapVisitor | null>(null)

  // Search state
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<MapVisitor[] | null>(null)
  const [searching, setSearching] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMapReady = useCallback((map: LeafletMap) => {
    mapRef.current = map
  }, [])

  const flyTo = useCallback((v: MapVisitor) => {
    if (!mapRef.current || !isPlottable(v)) return
    mapRef.current.flyTo([v.latitude, v.longitude], FLY_ZOOM, { duration: 1.1 })
  }, [])

  /** Clicking any visitor (online or offline): select + fly to its pin. */
  const handleRowClick = useCallback(
    (v: MapVisitor) => {
      setSelected(v)
      flyTo(v)
    },
    [flyTo]
  )

  // Debounced search across online + offline.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)

    const q = query.trim()
    if (!q) {
      setSearchResults(null)
      setSearching(false)
      return
    }

    setSearching(true)
    searchTimer.current = setTimeout(async () => {
      try {
        const data = await apiGet<VisitorSearchResponse>('/visitors/search', {
          q,
          status: '',
          page: 1,
          pageSize: 50,
        })
        setSearchResults(sortRoster(data.visitors || []))
      } catch (err) {
        console.error('Visitor search failed:', err)
        setSearchResults([])
      } finally {
        setSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [query])

  // Keep the open detail card in sync with the live roster (e.g. a selected
  // visitor going offline flips the duration readout to "last seen").
  useEffect(() => {
    if (!selected) return
    const fresh = visitors.find((v) => v.id === selected.id)
    if (fresh) setSelected(fresh)
  }, [visitors]) // eslint-disable-line react-hooks/exhaustive-deps

  const listVisitors = searchResults ?? visitors
  const showingSearch = searchResults !== null

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] h-full overflow-hidden">
      {/* Left panel: header counts, search, list */}
      <div className="flex flex-col border-r border-border min-h-0 bg-surface">
        {/* Header with live count + Live badge */}
        <div className="px-4 pt-4 pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-txt-primary">
                {onlineCount} online
              </h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success-light text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse-soft" />
                Live
              </span>
            </div>
            <span className="text-[11px] text-txt-tertiary">
              {visitors.length} total
            </span>
          </div>

          <div className="relative">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, city…"
              className="w-full pl-8 pr-8 py-2 rounded-lg text-sm bg-surface-secondary border border-border text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-txt-tertiary hover:text-txt-primary"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {showingSearch && (
            <p className="text-[11px] text-txt-tertiary mt-2">
              {searching
                ? 'Searching…'
                : `${listVisitors.length} result${listVisitors.length === 1 ? '' : 's'} (online + offline)`}
            </p>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0">
          {loading && !showingSearch ? (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner size="medium" />
            </div>
          ) : listVisitors.length === 0 ? (
            <div className="px-3 py-8 text-center">
              <p className="text-sm font-medium text-txt-secondary">
                {showingSearch ? 'No matching visitors' : 'No visitors yet'}
              </p>
              <p className="text-xs text-txt-tertiary mt-1">
                {showingSearch
                  ? 'Try a different name or city.'
                  : 'Visitors appear here as they browse your site.'}
              </p>
            </div>
          ) : (
            listVisitors.map((v) => (
              <VisitorRow
                key={v.id}
                visitor={v}
                active={selected?.id === v.id}
                onClick={() => handleRowClick(v)}
              />
            ))
          )}
        </div>
      </div>

      {/* Map area */}
      <div className="relative min-h-0">
        {error && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] px-3 py-1.5 rounded-lg text-xs font-medium bg-error-light text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 shadow-sm">
            {error}
          </div>
        )}

        {/* Empty state overlay when there are no visitors at all */}
        {!loading && visitors.length === 0 && (
          <div className="absolute inset-0 z-[450] flex items-center justify-center pointer-events-none">
            <div className="max-w-sm text-center px-6 py-8 rounded-xl bg-surface/90 backdrop-blur border border-border shadow-sm pointer-events-auto">
              <div className="w-12 h-12 mx-auto rounded-full bg-surface-tertiary flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.75 9.75 0 100-19.5A9.75 9.75 0 0012 21z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-txt-primary">There are no visitors.</p>
              <p className="text-xs text-txt-secondary mt-1">
                Nobody is currently browsing your website right now.
              </p>
            </div>
          </div>
        )}

        <VisitorMap
          visitors={visitors}
          onMapReady={handleMapReady}
          onSelectVisitor={handleRowClick}
        />

        {/* Detail card overlay */}
        {selected && (
          <div className="absolute bottom-4 right-4 z-[500] w-72 max-w-[calc(100%-2rem)]">
            <VisitorDetailCard
              visitor={selected}
              onClose={() => setSelected(null)}
              onViewDetails={() => setDetailVisitor(selected)}
            />
          </div>
        )}
      </div>

      {/* Full visitor details drawer */}
      {detailVisitor && (
        <VisitorDetailModal visitor={detailVisitor} onClose={() => setDetailVisitor(null)} />
      )}
    </div>
  )
}

export const VisitorMapView: React.FC = () => <VisitorMapViewInner />
